// Orchestration for the Commission-tab ADJUST (and the rep email for both Adjust
// and Delete). The Supabase-first work is createAdminAdjustment in supabase.js;
// this file adds the steps that may only happen AFTER it succeeded:
//   1. mirror the change into this device's SHARED_INVOICES cache (same call the
//      Orders approve uses: SHARED_INVOICES.applyAdjustment(rows))
//   2. record the commission correction (fresh reads; none when nothing was paid;
//      a failure never undoes the adjustment, the "Correction needed" scan stays
//      the backstop)
//   3. email the rep, fire-and-forget (never fails or delays the action)
import { SHARED_INVOICES } from "./RepPortal";
import { createAdminAdjustment, getAllProfiles } from "./supabase";
import { recordCorrectionAfterAction } from "./commissionAfterAction";
import { currentAdminIdentity } from "./commissionLedgerUi";
import { emailRepInvoiceChanged } from "./email";
import { resolveRepIdentity } from "./adminAdjustPure";
import { isBlockedStatus } from "./accountLifecycle";
import { fmtSignedMoney } from "./commissionCorrectionsView";

const defaultDeps = { SHARED_INVOICES, createAdminAdjustment, getAllProfiles, recordCorrectionAfterAction, currentAdminIdentity, emailRepInvoiceChanged };

// One sentence for the rep about their commission, from what the ledger did.
// paidRowsCount = how many paid payout rows the invoice had (fresh read). When it is > 0 and no
// correction was recorded, the sentence must NOT say the pending commission recalculates: the
// commission was already paid and the ledger found it already matching.
export function commissionSentenceForRep(kind, cres, paidRowsCount) {
  if (cres && cres.status === "recorded") {
    return `A commission correction of ${fmtSignedMoney(cres.delta)} was recorded because this invoice's commission had already been paid. It will be netted into your next payout.`;
  }
  if (cres && cres.status === "failed") {
    return "Your commission for this invoice is being reviewed by admin and any difference will be added to your next payout.";
  }
  if (Number(paidRowsCount) > 0) {
    return "The commission for this invoice had already been paid and the paid amount already matches, so no correction was needed.";
  }
  return kind === "void"
    ? "This invoice no longer counts toward your commission."
    : "Your pending commission for this invoice is recalculated automatically from the new total.";
}

/**
 * One sentence for the banner about the rep email, from what notifyRepInvoiceChanged
 * resolved with. Waits at most timeoutMs so the banner is never held up by a slow send;
 * a timeout is reported honestly as "not confirmed". NEVER throws.
 * @param {Promise<{sent:boolean, skipped?:string}>|null|undefined} emailPromise
 */
export async function repEmailSentence(emailPromise, timeoutMs = 4000) {
  if (!emailPromise) return "";
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve({ sent: false, skipped: "timeout" }), timeoutMs); });
  try {
    const r = await Promise.race([Promise.resolve(emailPromise), timeout]);
    if (r && r.sent === true) return "The rep was emailed.";
    if (r && r.skipped === "timeout") return "The email to the rep could not be confirmed yet (it may still arrive).";
    return `The rep was NOT emailed (${(r && r.skipped) || "unknown reason"}). Tell them directly.`;
  } catch (e) {
    console.warn("[repEmailSentence] email promise rejected:", e?.message || e);
    return "The rep was NOT emailed (the send failed). Tell them directly.";
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fire-and-forget email to the rep. Resolves the recipient from profiles (never
 * from the invoice cache). Unknown / blocked rep or unknown email: skipped with a
 * console warning. NEVER throws, NEVER rejects.
 * @returns {Promise<{sent:boolean, skipped?:string}>} resolves after the attempt (callers do not await it)
 */
export async function notifyRepInvoiceChanged(p, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  try {
    const { kind, invoice, rep, reason, oldTotal, newTotal, commissionNote } = p || {};
    if (!invoice || !invoice.id) { console.warn("[notifyRepInvoiceChanged] no invoice; email skipped"); return { sent: false, skipped: "no invoice" }; }
    let who = rep && rep.email ? rep : null;
    if (!who) {
      const profiles = await d.getAllProfiles();
      const r = resolveRepIdentity({ invoice, profiles });
      if (!r.ok) { console.warn("[notifyRepInvoiceChanged] rep not resolved for", invoice.id, "; email skipped:", r.reason); return { sent: false, skipped: r.reason }; }
      who = r.rep;
    }
    if (!who.email) { console.warn("[notifyRepInvoiceChanged] rep email unknown for", invoice.id, "; email skipped"); return { sent: false, skipped: "no email" }; }
    if (who.status && isBlockedStatus(who.status)) { console.warn("[notifyRepInvoiceChanged] rep account is not active for", invoice.id, "; email skipped"); return { sent: false, skipped: "blocked account" }; }
    const res = await d.emailRepInvoiceChanged({
      kind: kind === "void" ? "void" : "adjust",
      repName: who.name || invoice.rep || "", repEmail: who.email,
      invoiceId: String(invoice.id), customer: invoice.customer || "",
      oldTotal: Number(oldTotal) || 0, newTotal: Number(newTotal) || 0,
      reason: String(reason || ""), commissionNote: String(commissionNote || ""),
    });
    if (res && res.success === false) { console.warn("[notifyRepInvoiceChanged] email failed (the change is saved):", res.error); return { sent: false, skipped: "send failed" }; }
    return { sent: true };
  } catch (e) {
    console.warn("[notifyRepInvoiceChanged] threw (the change is saved):", e?.message || e);
    return { sent: false, skipped: "threw" };
  }
}

/**
 * Admin adjusts an unpaid rep-created invoice (Commission tab).
 * @param {{invoice:object, lines:Array, lookup:Function, reason:string, confirmedUnpaid:boolean}} p
 * @returns {Promise<object>} createAdminAdjustment's failure object unchanged, or
 *   { ok:true, oldTotal, newTotal, rep, requestId, correction:{status,delta?,message,detail?}, notices:string[] }
 */
export async function performAdminAdjust(p, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  const { invoice, lines, lookup, reason, confirmedUnpaid, legacyPaid } = p || {};
  // By-value snapshot: the admin's own realtime echo can mutate the live cache object during the awaits.
  let snap;
  try { snap = JSON.parse(JSON.stringify(invoice || null)); } catch (e) { snap = null; }
  if (!snap || !snap.id) return { ok: false, nothingChanged: true, code: "input", message: "Invoice not found." };
  const admin = await d.currentAdminIdentity();
  const res = await d.createAdminAdjustment({ invoice: snap, lines, lookup, reason, confirmedUnpaid, admin, legacyPaid: legacyPaid === true });
  if (!res || !res.ok) return res || { ok: false, nothingChanged: false, code: "unexpected", message: "Adjusting the invoice failed unexpectedly." };
  const notices = [];
  // 1. cache mirror (DB is the truth now; idempotent)
  try { d.SHARED_INVOICES.applyAdjustment(res.rows); }
  catch (e) { console.warn("[performAdminAdjust] SHARED_INVOICES.applyAdjustment local mirror failed:", e?.message || e); }
  // 2. commission correction (fresh reads; "none" when nothing was paid)
  const cres = await d.recordCorrectionAfterAction({
    invoiceId: snap.id, reason: `Invoice adjusted by admin (Commission tab, request ${res.requestId}): ${String(reason || "").trim()}`,
    source: "commission_tab", expect: "adjust", legacyPaid: legacyPaid === true,
  });
  if (cres.status === "failed") notices.push(cres.message);
  // 3. rep email, fire-and-forget: NOT awaited
  // The promise is returned so the banner can say "emailed" only when it really was (repEmailSentence);
  // nothing here waits for it.
  const emailPromise = Promise.resolve(notifyRepInvoiceChanged({
    kind: "adjust", invoice: snap, rep: res.rep, reason, oldTotal: res.oldTotal, newTotal: res.newTotal,
    commissionNote: commissionSentenceForRep("adjust", cres, res.paidRowsCount),
  }, d)).catch((e) => { console.warn("[performAdminAdjust] rep email promise rejected (the change is saved):", e?.message || e); return { sent: false, skipped: "threw" }; });
  return { ok: true, oldTotal: res.oldTotal, newTotal: res.newTotal, rep: res.rep, requestId: res.requestId, correction: cres, notices, paidRowsCount: res.paidRowsCount || 0, emailPromise };
}
